import type { Purchase } from "../types/Instance";

export interface PreorderInfo {
  preorderStatus: boolean;
  // ISO 8601 date string (YYYY-MM-DD). Only present when the backend returned
  // a valid future date for the preorder. May be undefined when status is true
  // but no usable date was configured (or was in the past / malformed).
  preorderShippingDate?: string;
}

// Accept the same truthy string values the backend honors, so this utility can
// parse Purchase.Details produced by any source — including non-Shopify
// retailers populating the same JSON shape via the standard API in future.
const truthyStrings = new Set(["true", "1", "yes"]);

const coerceBoolean = (value: unknown): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value > 0;
  if (typeof value === "string") {
    return truthyStrings.has(value.trim().toLowerCase());
  }
  return false;
};

// ISO date: YYYY-MM-DD (optionally followed by time part — we only keep the
// date portion for display). Anything else is rejected.
const isoDateRegex = /^(\d{4})-(\d{2})-(\d{2})(?:T|$)/;

const coerceShippingDate = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const match = value.trim().match(isoDateRegex);
  if (!match) return undefined;
  const [, y, m, d] = match;
  const iso = `${y}-${m}-${d}`;
  const parsed = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return undefined;
  // Past-date suppression is handled server-side for one-click exchange and
  // the exchange flow; persisted Purchase.Details may still contain a valid
  // date after an exchange was selected (Return Preview / Status). We keep
  // whatever the backend sent unchanged here.
  return iso;
};

/**
 * Safely extracts preorder info from a Purchase-like object. Returns null when
 * preorder is not present, when status is falsy, or when Details are missing
 * or malformed. Never throws.
 */
export const getPreorderInfo = (purchase: Pick<Purchase, "details"> | undefined | null): PreorderInfo | null => {
  if (!purchase) return null;
  let details: Record<string, unknown> | null = null;
  try {
    const raw = (purchase as Purchase).details as unknown;
    if (!raw) return null;
    if (typeof raw === "string") {
      details = JSON.parse(raw);
    } else if (typeof raw === "object") {
      details = raw as Record<string, unknown>;
    }
  } catch {
    return null;
  }

  if (!details || typeof details !== "object") return null;

  const status = coerceBoolean(details["preorder_status"]);
  if (!status) return null;

  const shippingDate = coerceShippingDate(details["preorder_shipping_date"]);
  return {
    preorderStatus: true,
    preorderShippingDate: shippingDate,
  };
};

export default getPreorderInfo;
